import { useLocation, useRoute } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BillingSection } from "@/components/settings/BillingSection";
import { PreferencesSection } from "@/components/settings/PreferencesSection";
import { ProfileSection } from "@/components/settings/ProfileSection";
import { SecuritySection } from "@/components/settings/SecuritySection";
import { PageHeader, PageShell } from "@/components/waggle/PageShell";
import { SECTIONS, isSectionId, type SectionId } from "@/lib/settingsSections";
import { cn } from "@/lib/utils";
import NotFound from "@/pages/not-found";

const SECTION_VIEWS: Record<SectionId, () => JSX.Element> = {
  profile: ProfileSection,
  security: SecuritySection,
  preferences: PreferencesSection,
  billing: BillingSection,
};

export default function Settings() {
  const [, params] = useRoute("/settings/:section");
  const [, setLocation] = useLocation();
  const section = params?.section;

  if (!isSectionId(section)) return <NotFound />;
  const View = SECTION_VIEWS[section];

  return (
    <PageShell>
      <PageHeader title="Settings" description="Your profile, sign-in, preferences and billing." />

      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="md:hidden">
          <Select value={section} onValueChange={(id) => setLocation(`/settings/${id}`)}>
            <SelectTrigger aria-label="Settings section" data-testid="settings-section-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECTIONS.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav aria-label="Settings" className="hidden w-56 shrink-0 md:block">
          <ul className="flex flex-col gap-0.5">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={s.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setLocation(s.href);
                  }}
                  aria-current={s.id === section ? "page" : undefined}
                  data-testid={`settings-nav-${s.id}`}
                  className={cn(
                    "block rounded-md px-2.5 py-2 text-sm font-semibold outline-hidden transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50",
                    s.id === section ? "bg-muted text-foreground" : "text-foreground/75",
                  )}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1">
          <View />
        </div>
      </div>
    </PageShell>
  );
}
